<template>
  <component
    :is="as"
    ref="button"
    class="q-focus flex flex-shrink-0 items-center justify-center rounded-full text-neutral-500 transition-colors disabled:pointer-events-none disabled:opacity-50"
    :class="{
      'h-7 w-7': size === 'base',
      '-m-1 p-1': size === 'sm',
      primary: variant === 'primary',
      default: variant === 'default',
      danger: variant === 'danger',
      dark: theme === 'dark',
    }"
  >
    <slot />
  </component>
</template>

<script lang="ts" setup>
import { ref } from 'vue'

withDefaults(
  defineProps<{
    as?: string
    variant?: 'primary' | 'default' | 'danger'
    size?: 'base' | 'sm'
    theme?: 'dark' | 'light'
  }>(),
  { size: 'base', variant: 'default', theme: 'light', as: 'button' }
)

const button = ref<HTMLButtonElement>()

defineExpose({
  focus(options?: FocusOptions) {
    if (button.value) {
      button.value.focus(options)
    }
  },
})
</script>

<style scoped>
:deep(svg) {
  @apply h-4 w-4;
}

.primary {
  @apply bg-white text-neutral-500 hover:bg-neutral-100;

  &:active,
  &[aria-pressed='true'] {
    background-color: theme('colors.brand.accent-blue');
    background-image: linear-gradient(
      180deg,
      rgb(0 0 0 / 0%) 0%,
      rgb(0 0 0 / 50%) 60%,
      rgb(0 0 0 / 70%) 100%
    );
    color: white;
  }
}

.default {
  @apply text-neutral-500 hover:bg-neutral-100 hover:text-neutral-700 active:bg-neutral-200;
}

.danger {
  @apply text-red-500 hover:bg-red-500 hover:text-white active:bg-red-600 active:text-white;
}

.dark {
  @apply text-white;

  &:hover {
    @apply bg-primary-500 text-white;
  }
}
</style>
